/**
 * Tenants API
 *
 * API functions for org-level (tenant) role management (FLOW-662), quota
 * status (FLOW-788), subscription billing (FLOW-700) and add-on token packs
 * (FLOW-851).
 *
 * Every id is encoded before interpolation. `tenantId` can originate in a URL
 * query (`BillingPage`'s `?tenant=`), and an unencoded `/` there would point the
 * request at an unrelated route under `/api` with the caller's session.
 */

import { api } from "./client";
import type {
  ChangeTenantRoleRequest,
  CheckoutSessionResponse,
  CreateCheckoutRequest,
  CreatePackRequest,
  TenantBillingStatus,
  TenantId,
  TenantMembershipRecord,
  TenantQuotaStatus,
  TenantRole,
  UserId,
} from "./types";

export async function changeTenantMemberRole(
  tenantId: TenantId,
  userId: UserId,
  role: TenantRole,
): Promise<TenantMembershipRecord> {
  const body: ChangeTenantRoleRequest = { role };
  return api.patch<TenantMembershipRecord>(
    `/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`,
    body,
  );
}

export async function getTenantQuota(
  tenantId: TenantId,
): Promise<TenantQuotaStatus> {
  return api.get<TenantQuotaStatus>(
    `/tenants/${encodeURIComponent(tenantId)}/quota`,
  );
}

export async function getTenantBilling(
  tenantId: TenantId,
): Promise<TenantBillingStatus> {
  return api.get<TenantBillingStatus>(
    `/tenants/${encodeURIComponent(tenantId)}/billing`,
  );
}

/** Opens a Stripe Checkout session; the caller navigates to the returned URL. */
export async function createBillingCheckout(
  tenantId: TenantId,
  req: CreateCheckoutRequest,
): Promise<CheckoutSessionResponse> {
  return api.post<CheckoutSessionResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/billing/checkout`,
    req,
  );
}

/**
 * Opens a one-time Stripe Checkout session for an add-on token pack
 * (FLOW-851); the caller navigates to the returned URL.
 */
export async function createPackCheckout(
  tenantId: TenantId,
  req: CreatePackRequest,
): Promise<CheckoutSessionResponse> {
  return api.post<CheckoutSessionResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/billing/packs`,
    req,
  );
}

/** Opens a Stripe Customer Portal session. The route takes no body. */
export async function createBillingPortal(
  tenantId: TenantId,
): Promise<CheckoutSessionResponse> {
  return api.post<CheckoutSessionResponse>(
    `/tenants/${encodeURIComponent(tenantId)}/billing/portal`,
    {},
  );
}
